import { PureAbility as Ability, AbilityBuilder } from '@casl/ability'
import { createContext, useContext, useMemo } from 'react'

import { useFeature } from 'src/components/common/FeatureFlags'
import { useUser } from 'src/utils/hooks/useUser'

import SimulationCtx, {
    READ_ONLY_SUBJECTS_IN_SIMULATION,
} from './simulationCtx'

/**
 * @type {import('./userDetailsContext').Permission[]}
 */
const DEFAULT_PERMISSIONS = [
    {
        action: 'read',
        subject: 'overview-dashboard',
    },
    {
        action: 'write',
        subject: 'overview-dashboard',
    },
    {
        action: 'read',
        subject: 'plan-overview-dashboard',
    },
    {
        action: 'read',
        subject: 'practice-overview-dashboard',
    },
    {
        action: 'read',
        subject: 'respond-overview-dashboard',
    },
    {
        action: 'read',
        subject: 'my-team',
    },
    {
        action: 'raci',
        subject: 'my-team',
    },
    {
        action: 'read',
        subject: 'incidents',
    },
    {
        action: 'read',
        subject: 'exercises',
    },
]

/**
 * Takes a permissions array and returns an ability instance for use
 * with CASL
 * @param {import('./userDetailsContext').Permission[]} permissions
 * @returns {Ability}
 */
export const defineUserAbilities = (permissions) => {
    const { can, rules } = new AbilityBuilder()

    permissions.forEach(({ action, subject }) => {
        can(action, subject)
    })

    return new Ability(rules)
}

/**
 * @param {{workRole:{organization_workroles:[]}}} role
 * @returns {boolean}
 */
function isBuiltInRole(role) {
    // there exists no records in users.organization_workrole for this role,
    // hence no rows are returned on the array relationship defined on organization_workroles property
    if (role.workRole.organization_workroles.length === 0) {
        return false
    } else {
        return true
    }
}

export const AbilityContext = createContext()

export const PermissionsContextProvider = ({ children }) => {
    const { isEnabled: isSimulationEnabled } = useContext(SimulationCtx)
    const hasRoleOverride = useFeature('roleOverride')
    const { workRoles } = useUser()

    const abilities = useMemo(() => {
        if (workRoles.length === 0) {
            // users has no roles
            return defineUserAbilities(DEFAULT_PERMISSIONS)
        } else {
            /**
             * @type {import('./userDetailsContext').Permission[]}
             */
            let permissions = workRoles.reduce((role_accumulator, role) => {
                // access role's permissions
                const role_permissions = role.workRole.permissions
                const org_permissions =
                    role.workRole.organization_workroles?.[0]?.permissions

                // apply builtin role's custom permissions
                // if they exist
                if (
                    isBuiltInRole(role) &&
                    hasRoleOverride &&
                    org_permissions?.length > 0
                ) {
                    return role_accumulator.concat(org_permissions)
                }
                // apply the role's default permissions
                else {
                    return role_accumulator.concat(role_permissions)
                }
            }, [])

            // if in simulation mode disallow certains actions

            if (isSimulationEnabled) {
                // if a user has write access to a subject in the list `READ_ONLY_SUBJECTS_IN_SIMULATION`
                // then remove it from the effective permissions in simulation mode
                // i.e. leaving just read access
                permissions = permissions.filter((permission) => {
                    // Keep all permissions that aren't in the READ_ONLY_SUBJECTS_IN_SIMULATION list
                    if (
                        !READ_ONLY_SUBJECTS_IN_SIMULATION.includes(
                            permission.subject
                        )
                    ) {
                        return true
                    }
                    // Keep read permissions for subjects in the READ_ONLY_SUBJECTS_IN_SIMULATION list
                    if (permission.action === 'read') {
                        return true
                    }
                    // Filter out write permissions for subjects in the READ_ONLY_SUBJECTS_IN_SIMULATION list
                    return false
                })
            }

            return defineUserAbilities(permissions)
        }
    }, [hasRoleOverride, isSimulationEnabled, workRoles])

    return (
        <AbilityContext.Provider value={abilities}>
            {children}
        </AbilityContext.Provider>
    )
}
